import type { ComponentProps } from 'react'
import { FieldInfo, hasFieldError, useFieldContext, useFormReadOnly, useIsFieldOptional } from './shared'
import { Input } from '../../ui/input'
import { FieldLabelRow } from './field-label-row'
import { cn, formatDatetime } from '@/lib/utils'

export default function DateTimeField({
  label,
  className,
  optional,
  ...props
}: ComponentProps<'input'> & { label: string; optional?: boolean }) {
  const field = useFieldContext<string | null | undefined>()
  const formReadOnly = useFormReadOnly()
  const isReadOnly = props.readOnly ?? formReadOnly
  const isOptional = useIsFieldOptional(field.name, optional)
  const formattedValue = field.state.value ? formatDatetime(field.state.value) : ''
  const readOnlyDisplay = field.state.value ? new Date(field.state.value).toLocaleString() : '-'

  return (
    <div className={className}>
      <div className="grid gap-2">
        {label && (
          <FieldLabelRow
            label={label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            optional={isOptional}
          />
        )}
        <Input
          type={isReadOnly ? 'text' : 'datetime-local'}
          id={field.name}
          name={field.name}
          aria-invalid={hasFieldError(field.state.meta)}
          value={isReadOnly ? readOnlyDisplay : formattedValue}
          onBlur={field.handleBlur}
          onChange={(e) => field.handleChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
          {...props}
          readOnly={isReadOnly}
          tabIndex={isReadOnly ? -1 : props.tabIndex}
          className={cn(
            isReadOnly &&
              'border-transparent bg-transparent px-0 focus-visible:border-transparent focus-visible:ring-0 dark:bg-transparent',
          )}
        />
      </div>
      <FieldInfo fieldState={field.state} />
    </div>
  )
}
